import { ref } from 'vue'
import { defineStore } from 'pinia'

// Storage keys
const STORAGE_KEYS = {
  RESULTS: 'searchResults',
  PARAMS: 'searchParams',
  MODE: 'tripFinderMode',
  TIMESTAMP: 'searchResultsTimestamp'
}

// Cache TTL (Time To Live) - 1 hour in milliseconds
export const CACHE_TTL = 60 * 60 * 1000

export const useSearchResultsStore = defineStore('searchResults', () => {
  const searchResults = ref([])
  const searchParams = ref(null)
  const tripFinderMode = ref('build')
  const isLoading = ref(false)

  // Check if cached data is still valid
  const isCacheValid = () => {
    const timestamp = localStorage.getItem(STORAGE_KEYS.TIMESTAMP)
    if (!timestamp) return false

    const age = Date.now() - parseInt(timestamp, 10)
    return age < CACHE_TTL
  }

  // Load search results from storage (sessionStorage first, then localStorage backup)
  const loadSearchResults = () => {
    try {
      // Try sessionStorage first (always fresh)
      let storedResults = sessionStorage.getItem(STORAGE_KEYS.RESULTS)
      let storedParams = sessionStorage.getItem(STORAGE_KEYS.PARAMS)
      let storedMode = sessionStorage.getItem(STORAGE_KEYS.MODE)

      // If sessionStorage is empty, try localStorage as backup (with TTL check)
      if (!storedResults && isCacheValid()) {
        if (import.meta.env.DEV) console.debug('[SearchResultsStore] SessionStorage empty, loading from localStorage backup')
        storedResults = localStorage.getItem(STORAGE_KEYS.RESULTS)
        storedParams = localStorage.getItem(STORAGE_KEYS.PARAMS)
        storedMode = localStorage.getItem(STORAGE_KEYS.MODE)

        // Restore to sessionStorage for current session
        if (storedResults) {
          sessionStorage.setItem(STORAGE_KEYS.RESULTS, storedResults)
          sessionStorage.setItem(STORAGE_KEYS.PARAMS, storedParams || '{}')
          sessionStorage.setItem(STORAGE_KEYS.MODE, storedMode || 'build')
        }
      }

      if (storedResults) {
        searchResults.value = JSON.parse(storedResults)
        if (import.meta.env.DEV) console.debug('[SearchResultsStore] Loaded results:', searchResults.value.length)
      }

      if (storedParams) {
        searchParams.value = JSON.parse(storedParams)
      }

      if (storedMode) {
        tripFinderMode.value = storedMode
      }

      return searchResults.value.length > 0
    } catch (err) {
      console.error('[SearchResultsStore] Error loading search results:', err)
      return false
    }
  }

  // Save search results to storage (both sessionStorage and localStorage)
  const saveSearchResults = (results, params, mode = 'build') => {
    try {
      const resultsJson = JSON.stringify(results)
      const paramsJson = JSON.stringify(params)

      // Save to sessionStorage (current session)
      sessionStorage.setItem(STORAGE_KEYS.RESULTS, resultsJson)
      sessionStorage.setItem(STORAGE_KEYS.PARAMS, paramsJson)
      sessionStorage.setItem(STORAGE_KEYS.MODE, mode)

      // Save to localStorage as backup (with timestamp for TTL)
      localStorage.setItem(STORAGE_KEYS.RESULTS, resultsJson)
      localStorage.setItem(STORAGE_KEYS.PARAMS, paramsJson)
      localStorage.setItem(STORAGE_KEYS.MODE, mode)
      localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString())

      // Update reactive state
      searchResults.value = results
      searchParams.value = params
      tripFinderMode.value = mode

      if (import.meta.env.DEV) {
        console.debug(`[SearchResultsStore] Saved results: ${results.length} (mode: ${mode})`)
        console.debug('[SearchResultsStore] Stored params:', params)
      }
    } catch (err) {
      console.error('[SearchResultsStore] Error saving search results:', err)
    }
  }

  // Clear search results from all storage
  const clearSearchResults = () => {
    try {
      // Clear sessionStorage
      sessionStorage.removeItem(STORAGE_KEYS.RESULTS)
      sessionStorage.removeItem(STORAGE_KEYS.PARAMS)
      sessionStorage.removeItem(STORAGE_KEYS.MODE)

      // Clear localStorage
      localStorage.removeItem(STORAGE_KEYS.RESULTS)
      localStorage.removeItem(STORAGE_KEYS.PARAMS)
      localStorage.removeItem(STORAGE_KEYS.MODE)
      localStorage.removeItem(STORAGE_KEYS.TIMESTAMP)

      // Clear reactive state
      searchResults.value = []
      searchParams.value = null
      tripFinderMode.value = 'build'

      if (import.meta.env.DEV) console.debug('[SearchResultsStore] Cleared all search results')
    } catch (err) {
      console.error('[SearchResultsStore] Error clearing search results:', err)
    }
  }

  // Check if we have valid search results
  const hasValidResults = () => {
    return searchResults.value.length > 0
  }

  // Get search results count
  const getResultsCount = () => {
    return searchResults.value.length
  }

  return {
    searchResults,
    searchParams,
    tripFinderMode,
    isLoading,
    loadSearchResults,
    saveSearchResults,
    clearSearchResults,
    hasValidResults,
    getResultsCount
  }
})
